import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar, faCheck, faWarning } from '@rq-ratings/pro-solid-svg-icons'
import React, { createContext, useState } from 'react'
import { Alert, Badge } from 'react-bootstrap'

import LoadingCard from '../../../../components/misc/LoadingCard'
import { useCompanyNylasIntegrationQuery } from '../../../../hooks/useCompanyNylasIntegrationQuery'
import { useInvalidateQueries } from '../../../../hooks/useInvalidateQueries'
import { EMAIL_ADDRESSES, NylasRedirect } from '../../../../lib/constants'
import { QUERY_KEYS } from '../../../../lib/queryKeys'
import { INTEGRATION_SERVICE } from '../../../../types/misc'
import { useNylasCalendarCompanyUserIntegrationQuery } from '../../hooks/useNylasCalendarCompanyUserIntegrationQuery'
import { useNylasEmailCompanyUserIntegrationQuery } from '../../hooks/useNylasEmailCompanyUserIntegrationQuery'
import ConnectedAccountAlert from './ConnectedAccountAlert'
import ConnectEmailAccountsModal from './ConnectEmailAccountsModal'
import ConnectUserAccountButton from './ConnectUserAccountButton'
import DeleteIntegrationButton from './DeleteIntegrationButton'
import NylasRedirectContext from './NylasRedirectContext'

const PERSONAL_EMAIL_TOOLTIP =
  "This is how your personal comms will be sent. To send via your company's shared email instead, disconnect this email account."

interface Props {
  nylasRedirect: NylasRedirect
}

const ConnectUserAccounts: React.FC<Props> = ({ nylasRedirect }) => {
  createContext(nylasRedirect)

  const invalidateQueries = useInvalidateQueries()

  const [isDisconnectInProgress, setIsDisconnectInProgress] =
    useState<boolean>(false)

  const companyNylasIntegrationQuery = useCompanyNylasIntegrationQuery()
  const companyNylasIntegration = companyNylasIntegrationQuery.data

  const nylasCalendarCompanyUserIntegrationQuery =
    useNylasCalendarCompanyUserIntegrationQuery()

  const nylasEmailCompanyUserIntegrationQuery =
    useNylasEmailCompanyUserIntegrationQuery()

  if (
    nylasCalendarCompanyUserIntegrationQuery.isPending ||
    nylasEmailCompanyUserIntegrationQuery.isPending
  ) {
    return <LoadingCard />
  }

  const nylasEmailCompanyUserIntegration =
    nylasEmailCompanyUserIntegrationQuery.data

  const companyHasEmailAuth =
    companyNylasIntegration?.enabled &&
    companyNylasIntegration?.secureConfigValid &&
    !!companyNylasIntegration?.config.companyEmail?.grantId

  const companyUserHasEmailAuth =
    !!nylasEmailCompanyUserIntegration &&
    nylasEmailCompanyUserIntegration.enabled &&
    nylasEmailCompanyUserIntegration.secureConfigValid

  const nylasCalendarCompanyUserIntegration =
    nylasCalendarCompanyUserIntegrationQuery.data

  const hasCalendarAuth =
    !!nylasCalendarCompanyUserIntegration &&
    nylasCalendarCompanyUserIntegration.enabled &&
    nylasCalendarCompanyUserIntegration.secureConfigValid

  async function handleDisconnectCalendar() {
    setIsDisconnectInProgress(false)

    const queryKeysToInvalidate = [
      QUERY_KEYS.companyNylasIntegration,
      QUERY_KEYS.companyUserIntegrationByType(
        INTEGRATION_SERVICE.nylasCalendar,
      ),
      QUERY_KEYS.companyCompanyUserIntegrationsByType(
        INTEGRATION_SERVICE.nylasCalendar,
      ),

      QUERY_KEYS.shareableIndividualReferralLinks(),
      QUERY_KEYS.shareableRoundRobinReferralLinks(),
    ]

    await invalidateQueries(queryKeysToInvalidate)
  }

  return (
    <NylasRedirectContext.Provider value={nylasRedirect}>
      <div className="d-flex flex-md-row flex-column gap-4">
        <section className="flex-1">
          <div className="border-box rounded p-4">
            <h3 className="d-flex gap-3 align-items-start justify-content-between">
              Email
              {(companyUserHasEmailAuth || companyHasEmailAuth) && (
                <Badge pill bg="success" className="fs-5">
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Connected
                </Badge>
              )}
            </h3>

            <p>
              Connect your personal or shared email so RQ messages come from
              your address—not {EMAIL_ADDRESSES.rqHello}. Referrals and invites
              feel more personal.
            </p>

            <div className="d-flex flex-column gap-3 align-items-start">
              {companyUserHasEmailAuth && (
                // No need to show as "current" if the shared account isn't also connected
                <ConnectedAccountAlert
                  isCurrent={companyHasEmailAuth}
                  tooltipText={PERSONAL_EMAIL_TOOLTIP}
                >
                  {nylasEmailCompanyUserIntegration.config.email} is connected
                  as your personal email account.
                </ConnectedAccountAlert>
              )}

              {companyHasEmailAuth && (
                <ConnectedAccountAlert>
                  {companyNylasIntegration.config.companyEmail?.email} is
                  connected as your shared email account.
                </ConnectedAccountAlert>
              )}

              <ConnectEmailAccountsModal
                nylasEmailCompanyUserIntegration={
                  nylasEmailCompanyUserIntegration
                }
                companyNylasIntegration={companyNylasIntegration}
                isDisconnectInProgress={isDisconnectInProgress}
                onUpdateDisconnectInProgress={setIsDisconnectInProgress}
              />
            </div>
          </div>
        </section>

        <section className="flex-1">
          <div className="border-box rounded p-4">
            <h3 className="d-flex gap-3 align-items-start justify-content-between">
              Calendar
              {hasCalendarAuth && (
                <Badge pill bg="success" className="fs-5">
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  Connected
                </Badge>
              )}
            </h3>

            <p>
              Sync your calendar to allow direct meeting bookings and referral
              scheduling. RQ respects your working hours and availability.
            </p>

            <div className="d-flex flex-column gap-3 align-items-start">
              {hasCalendarAuth && (
                <ConnectedAccountAlert>
                  {nylasCalendarCompanyUserIntegration.config.email} is
                  connected
                </ConnectedAccountAlert>
              )}

              {!hasCalendarAuth && (
                <ConnectUserAccountButton
                  icon={faCalendar}
                  text="Connect calendar"
                  integratedService={INTEGRATION_SERVICE.nylasCalendar}
                />
              )}

              {hasCalendarAuth && (
                <div>
                  <DeleteIntegrationButton
                    isCompanySchedulerOwner={
                      nylasCalendarCompanyUserIntegration.config
                        .isOwnerForCompanyConfig
                    }
                    companyUserIntegrationId={
                      nylasCalendarCompanyUserIntegration.id
                    }
                    disabled={isDisconnectInProgress}
                    integratedService={INTEGRATION_SERVICE.nylasCalendar}
                    onMutationStarted={() => setIsDisconnectInProgress(true)}
                    onError={() => setIsDisconnectInProgress(false)}
                    onDeleted={handleDisconnectCalendar}
                  />

                  {nylasCalendarCompanyUserIntegration.config
                    .isOwnerForCompanyConfig && (
                    <Alert variant="warning" className="m-0 p-3 mt-3">
                      <FontAwesomeIcon
                        icon={faWarning}
                        className="me-2 fs-3 text-warning"
                      />

                      <p className="m-0">
                        You are the owner of your company's scheduler.
                        Disconnecting your calendar account will delete your
                        company's scheduler configuration, and disable that
                        scheduler.
                      </p>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </NylasRedirectContext.Provider>
  )
}

export default ConnectUserAccounts
